import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { getPresidents } from "@/lib/api";
import { T } from "@/context/LanguageContext";
import { Seo } from "@/components/site/Seo";
import { SectionHeading } from "@/components/home/ExploreByState";

const PARTY_COLOR = {
  Republican: "#c8102e", Democratic: "#0a2a5e", Whig: "#b8860b",
  "Democratic-Republican": "#4b6a2f", Federalist: "#5a3e8e", Unaffiliated: "#555555",
};
const initials = (name) => name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("");

export default function Presidents() {
  const [list, setList] = useState([]);
  const [party, setParty] = useState("All");

  useEffect(() => { getPresidents().then((d) => setList(d.presidents)).catch(() => {}); }, []);

  const parties = ["All", ...Array.from(new Set(list.map((p) => p.party)))];
  const shown = party === "All" ? list : list.filter((p) => p.party === party);

  return (
    <div className="mx-auto max-w-[1500px] px-4 py-12" data-testid="presidents-page">
      <Seo
        title="U.S. Presidents — Complete Directory"
        path="/presidents"
        description="Every President of the United States in order, with portraits, party and term. Explore all 46 presidencies on USAPulse, each linking to an in-depth profile."
      />
      <SectionHeading kicker="The Executive · Leaders of the Nation" title="U.S. Presidents" />
      <p className="-mt-4 mb-6 max-w-2xl text-muted-foreground">
        <T>Every president in the nation's history, in order. Grover Cleveland served two non-consecutive terms (22nd & 24th), so 45 individuals have held the office through the 46th presidency.</T>
      </p>

      <div className="sticky top-11 z-20 -mx-4 mb-8 flex flex-wrap gap-2 bg-white/90 px-4 py-3 backdrop-blur">
        {parties.map((c) => (
          <button
            key={c}
            onClick={() => setParty(c)}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${party === c ? "bg-navy text-white" : "bg-parchment text-navy hover:bg-navy/10"}`}
            data-testid={`president-party-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
          >
            <T>{c}</T>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {shown.map((p, i) => (
          <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 12) * 0.02, duration: 0.3 }}>
            <Link to={`/people/${p.id}`} className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-border bg-white transition-all hover:-translate-y-1 hover:border-navy/30 hover:shadow-xl" data-testid={`president-card-${p.id}`}>
              <span className="absolute left-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-navy text-xs font-bold text-white ring-2 ring-white">{p.president_number}</span>
              <span className="absolute right-0 top-0 h-1.5 w-full" style={{ background: PARTY_COLOR[p.party] || "#555" }} />
              {p.wiki_thumb ? (
                <img src={p.wiki_thumb} alt={p.name} loading="lazy" className="h-44 w-full object-cover object-top" />
              ) : (
                <span className="flex h-44 w-full items-center justify-center bg-navy font-display text-3xl font-black text-white">{initials(p.name)}</span>
              )}
              <div className="flex flex-1 flex-col p-3">
                <div className="font-display text-sm font-bold leading-tight text-navy group-hover:text-oldglory-red">{p.name}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{p.term}</div>
                <span className="mt-2 inline-flex w-fit items-center gap-1.5 text-[11px] font-semibold" style={{ color: PARTY_COLOR[p.party] || "#555" }}>
                  <span className="h-2 w-2 rounded-full" style={{ background: PARTY_COLOR[p.party] || "#555" }} />
                  {p.party}
                </span>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
